import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Flex,
  Spinner,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  Image,
  Textarea,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const PAGE_SIZE = 20;
const COLUMN_MAP = {
  id: "ID",
  name: "레이블명",
  user: "사용자",
  created_date: "생성일",
};

const Labels = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();
  const [inputPage, setInputPage] = useState(page.toString());

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    let query = supabase
      .from("label")
      .select("*", { count: "exact" })
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (search) {
      query = query.or(`name.ilike.%${search}%,user.ilike.%${search}%`);
    }

    const { data: rows, count, error } = await query;
    if (!error) {
      setData(rows || []);
      setTotalPages(Math.max(1, Math.ceil((count || 1) / PAGE_SIZE)));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [search, page]);

  useEffect(() => {
    setInputPage(String(page));
  }, [page]);

  const handleRowClick = (row) => {
    setSelected(row);
    setEditFields(row);
    setModalOpen(true);
  };

  const handleEditChange = (key, value) => {
    setEditFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selected) return;
    const { error } = await supabase
      .from("label")
      .update(editFields)
      .eq("id", selected.id);
    if (!error) {
      toast({ title: "수정 완료", status: "success" });
      setModalOpen(false);
      fetchData();
    } else {
      toast({
        title: "수정 실패",
        status: "error",
        description: error.message,
      });
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `label-logo-${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("label-logo")
        .upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: urlData, error: urlError } = await supabase.storage
        .from("label-logo")
        .createSignedUrl(fileName, 60 * 60 * 24 * 7);
      if (urlError || !urlData?.signedUrl) throw urlError;
      setEditFields((prev) => ({ ...prev, logo: urlData.signedUrl }));
      toast({ title: "로고 업로드 완료", status: "success" });
    } catch (error) {
      toast({ title: "로고 업로드 실패", status: "error" });
    }
  };

  const handlePageInput = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) setInputPage(value);
  };

  const handlePageGo = () => {
    const num = parseInt(inputPage, 10);
    if (num >= 1 && num <= totalPages) setPage(num);
    else
      toast({ title: "올바른 페이지 번호를 입력하세요.", status: "warning" });
  };

  return (
    <Box maxW="1400px" mx="auto" py={8}>
      <Flex mb={4} gap={2} align="center">
        <Input
          placeholder="레이블명, 유저 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          width="300px"
        />
        <Button colorScheme="blue" onClick={handleSearch}>
          검색
        </Button>
      </Flex>
      <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="md">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>ID</Th>
              <Th>레이블명</Th>
              <Th>사용자</Th>
              <Th>생성일</Th>
              <Th>관리</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : data.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Text color="gray.400">데이터 없음</Text>
                </Td>
              </Tr>
            ) : (
              data.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.id}</Td>
                  <Td>
                    <Flex align="center" gap={3}>
                      {row.logo ? (
                        <Image
                          src={row.logo}
                          alt="logo"
                          boxSize="48px"
                          borderRadius="md"
                        />
                      ) : (
                        <Box boxSize="48px" bg="gray.100" borderRadius="md" />
                      )}
                      <Text>{row.name}</Text>
                    </Flex>
                  </Td>
                  <Td>{row.user}</Td>
                  <Td>
                    {row.created_date
                      ? new Date(row.created_date).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => handleRowClick(row)}
                    >
                      수정
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <Flex justify="center" mt={4} gap={2} align="center">
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          이전
        </Button>
        <Input
          value={inputPage}
          onChange={handlePageInput}
          onBlur={handlePageGo}
          size="sm"
          width="50px"
          textAlign="center"
          fontSize="sm"
          placeholder="페이지"
        />
        <Button size="sm" onClick={handlePageGo}>
          이동
        </Button>
        <Text fontSize="sm" px={2}>
          / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          다음
        </Button>
      </Flex>
      {/* 수정 모달 */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>레이블 정보 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <Flex align="center" gap={3}>
                <Box minW="120px">로고</Box>
                <Flex align="center" gap={2}>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleLogoUpload(e.target.files[0])}
                  />
                  {editFields.logo && (
                    <Image
                      src={editFields.logo}
                      alt="logo"
                      boxSize="48px"
                      borderRadius="md"
                    />
                  )}
                </Flex>
              </Flex>
              <Flex align="center" gap={3}>
                <Box minW="120px">레이블명</Box>
                <Input
                  value={editFields.name ?? ""}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                />
              </Flex>
              <Flex align="center" gap={3}>
                <Box minW="120px">설명</Box>
                <Textarea
                  value={editFields.description ?? ""}
                  onChange={(e) =>
                    handleEditChange("description", e.target.value)
                  }
                  minH="100px"
                />
              </Flex>
              <Flex align="center" gap={3}>
                <Box minW="120px">유저</Box>
                <Input
                  value={editFields.user ?? ""}
                  onChange={(e) => handleEditChange("user", e.target.value)}
                />
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleSave}>
              저장
            </Button>
            <Button ml={3} onClick={() => setModalOpen(false)}>
              취소
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Labels;
